<template>
  <div>
    <div v-if="checkoutStep === 1" class="flex items-start flex-col">
      <h1 class="w-full font-bold text-3xl mb-2">Pasul 1 - Facturare</h1>
      <div class="max-w-md">
        <select-input v-model="billingSelectedAddressId" v-if="$page.auth.user && $page.auth.user.addresses.length"
                      class="mt-8 pb-8">
          <option disabled selected :value="0">
            {{ $t('misc.choose_address') }}
          </option>
          <option v-for="address in $page.auth.user.addresses" :key="address.id" :value="address.id">
            {{ address.company && address.cif ? 'Company' : 'Personal' }} - {{ address.address }},
            {{ address.postal_code }}
          </option>
        </select-input>
      </div>

      <form class="mt-8 w-full" @submit.prevent="nextStep()">
        <div class="lg:grid lg:grid-flow-col xl:grid xl:grid-flow-col  md:grid md:grid-flow-col">
          <div class="pb-8 px-10 max-w-md lg:mr-16 xl:mr-16 md:mr-16">
            <text-input v-model="billing.firstName" :required="true" :errors="$page.errors.first_name" class="mt-4"
                        label="Prenume" type="text" autofocus autocapitalize="on"/>
            <text-input v-model="billing.lastName" :required="true" :errors="$page.errors.last_name" class="mt-4"
                        label="Nume" type="text" autocapitalize="on"/>
            <text-input v-model="billing.phoneNumber" :required="true" :errors="$page.errors.phone_number" class="mt-4"
                        label="Telefon" type="text"/>
            <text-input v-if="!$page.auth.user" v-model="billing.email" :required="true" :errors="$page.errors.email"
                        class="mt-4"
                        label="Email" type="text"/>
            <text-input v-model="billing.company" :errors="$page.errors.company" class="mt-4" label="Companie"
                        type="text"/>
            <text-input v-model="billing.cif" :errors="$page.errors.cif" class="mt-4" label="CIF" type="text"/>
          </div>
          <div class="pb-8 max-w-md">
            <text-input v-model="billing.address" :required="true" :errors="$page.errors.address" class="mt-4"
                        label="Adresă" type="text"/>
            <text-input v-model="billing.postalCode" :required="true" :errors="$page.errors.postal_code" class="mt-4"
                        label="Cod poștal" type="text"/>
            <select-input v-if="$page.auth.user && billing.country != null" @input="getCountryRegions"
                          ref="billing-country" :key="billing.country"
                          v-model="billing.country" :required="true" :errors="$page.errors.country" class="mt-4"
                          label="Țară">
              <option v-if="billing.country" v-for="country in countries" :key="country.code" :value="country.code"
                      v-bind:selected="country.code == billing.country.toUpperCase()">{{ country.name }}
              </option>
            </select-input>
            <select-input v-else @input="getCountryRegions" ref="billing-country" :key="billing.country"
                          v-model="billing.country" :required="true" :errors="$page.errors.country" class="mt-4"
                          label="Țară">
              <option v-for="country in countries" :key="country.code" :value="country.code">
                {{ country.name }}
              </option>
            </select-input>
            <select-input v-if="billing_regions && billing_regions.length" :key="billing.regionId"
                          v-model="billing.regionId" :required="true" :errors="$page.errors.region" class="mt-4"
                          label="Regiune">
              <option v-for="region in billing_regions" :key="region.id" :value="region.id">{{ region.name }}</option>
            </select-input>
            <text-input v-else v-model="billing.region" :required="true" :value="null" :errors="$page.errors.region"
                        class="mt-4"
                        label="Regiune" type="text"/>
            <text-input v-model="billing.city" :required="true" :errors="$page.errors.city" class="mt-4" label="Oraș"
                        type="text"/>
          </div>
        </div>
        <div class="w-full mt-4 py-4 bg-grey-100 border-grey-200 flex justify-end">
          <span class="text-2xl">Total: <span class="font-bold">&euro; {{
              number_format($page.cart.total, 0, 0, '.')
            }}</span></span>
        </div>
        <div class="w-full py-4 bg-grey-100 border-grey-200 flex justify-end">
          <svg class="w-4 h-4 mt-2" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
               viewBox="0 0 24 24" stroke="currentColor">
            <path d="M15 19l-7-7 7-7"></path>
          </svg>
          <a :href="route('shop.cart.view')"
             class="btn-gray text-gray-700 bg-transparent hover:bg-transparent hover:text-gray-900"
             style="padding: 6px 12px 6px 0px; color: currentColor !important; ">
            <span>Înapoi la coș</span>
          </a>
          <button type="submit" class="btn-gray">
            <span>Pasul următor</span>
          </button>
        </div>
      </form>
    </div>
    <div v-if="checkoutStep === 2" class="flex items-center flex-wrap">
      <h1 class="w-full font-bold text-3xl mb-2 mt-8">Pasul 2 - Livrare</h1>
      <div class="w-full max-w-2xl">
        <select-input v-model="deliveryOption" :key="deliveryOption" @change="setDelivery(this.value)" :required="true"
                      :errors="$page.errors.delivery_option" class="mt-4" label="Mod de livrare">
          <option :value="$page.constants.order.delivery_options.showroom.value">
            {{ $page.constants.order.delivery_options.showroom.text }}
          </option>
          <option :value="$page.constants.order.delivery_options.romania.value">
            {{ $page.constants.order.delivery_options.romania.text }}
          </option>
        </select-input>
      </div>
      <div v-if="deliveryOption == $page.constants.order.delivery_options.romania.value" class="w-full mt-8">
        <p v-html="$t('cart.delivery_text')"></p>
        <div class="max-w-2xl">
          <select-input v-model="shippingSelectedAddressId" v-if="$page.auth.user && $page.auth.user.addresses.length"
                        class="mt-4 pb-8" label="Unde livram?">
            <option :value=null>
              Aceeasi cu adresa de facturare
            </option>
            <option v-for="address in $page.auth.user.addresses" :key="address.id" :value="address.id">
              {{ address.company && address.cif ? 'Company' : 'Personal' }} - {{ address.address }},
              {{ address.postal_code }}
            </option>
            <option :value="0">
              Adaugă adresă
            </option>
          </select-input>
          <select-input v-else v-model="showAddressForm" class="mt-4 pb-8" label="Unde livram?">
            <option :value=false>
              Aceeasi cu adresa de facturare
            </option>
            <option :value=true>
              Adaugă adresă
            </option>
          </select-input>
        </div>
        <div v-if="showAddressForm" class="flex items-start flex-col">
          <form class="w-full">
            <div class="lg:grid lg:grid-flow-col xl:grid xl:grid-flow-col  md:grid md:grid-flow-col">
              <div class="pb-8 px-10 max-w-md lg:mr-16 xl:mr-16 md:mr-16">
                <text-input v-model="shipping.firstName" :required="true" :errors="$page.errors.first_name"
                            class="mt-4"
                            label="Prenume" type="text" autofocus autocapitalize="on"/>
                <text-input v-model="shipping.lastName" :required="true" :errors="$page.errors.last_name" class="mt-4"
                            label="Nume" type="text" autocapitalize="on"/>
                <text-input v-model="shipping.phoneNumber" :required="true" :errors="$page.errors.phone_number"
                            class="mt-4" label="Telefon" type="text"/>
                <text-input v-model="shipping.company" :errors="$page.errors.company" class="mt-4" label="Companie"
                            type="text"/>
                <text-input v-model="shipping.cif" :errors="$page.errors.cif" class="mt-4" label="CIF" type="text"/>
              </div>
              <div class="pb-8 px-10 max-w-md lg:mr-16 xl:mr-16 md:mr-16">
                <text-input v-model="shipping.address" :required="true" :errors="$page.errors.address" class="mt-4"
                            label="Adresă" type="text"/>
                <text-input v-model="shipping.postalCode" :required="true" :errors="$page.errors.postal_code"
                            class="mt-4"
                            label="Cod poștal" type="text"/>
                <select-input @input="getCountryRegions('RO', 'RO')" ref="shipping-country"
                              :key="shipping.country"
                              v-model="shipping.country" :required="true" :errors="$page.errors.country" class="mt-4"
                              label="Țară">
                  <option v-for="country in countries" v-if="country.code == 'RO'" :key="country.code"
                          :value="country.code">
                    {{ country.name }}
                  </option>
                </select-input>
                <select-input v-if="shipping_regions && shipping_regions.length" :key="shipping.regionId"
                              v-model="shipping.regionId" :required="true" :errors="$page.errors.region" class="mt-4"
                              label="Regiune">
                  <option v-for="region in shipping_regions" :key="region.id" :value="region.id">{{
                      region.name
                    }}
                  </option>
                </select-input>
                <text-input v-else v-model="billing.region" :required="true" :errors="$page.errors.region"
                            class="mt-4"
                            label="Regiune" type="text"/>

                <text-input v-model="shipping.city" :required="true" :errors="$page.errors.city" class="mt-4"
                            label="City"
                            type="text"/>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div v-if="deliveryOption == $page.constants.order.delivery_options.international.value" class="w-full mt-8">
        <div class="max-w-lg">
          <p>Costul transportului va fi calculat în funcție de adresa de livrare, de numărul și dimensiunea loturilor
            achiziționat. Vei
            primi, în cel mai scurt timp, un email cu toate detaliile</p>
          <select-input v-model="shippingSelectedAddressId" v-if="$page.auth.user && $page.auth.user.addresses.length"
                        class="mt-4 pb-8" label="Unde livram?">
            <option :value=null>
              Selectează/adaugă adresă
            </option>
            <option v-for="address in $page.auth.user.addresses" :key="address.id" :value="address.id">
              {{ address.company && address.cif ? 'Company' : 'Personal' }} - {{ address.address }},
              {{ address.postal_code }}
            </option>
            <option :value="0">
              Adaugă adresă
            </option>
          </select-input>
          <select-input v-else v-model="showAddressForm" class="mt-4 pb-8" label="Unde livram?">
            <option :value=false>
              Aceeasi cu adresa de facturare
            </option>
            <option :value=true>
              Adaugă adresă
            </option>
          </select-input>
          <form v-if="showAddressForm" class="mt-8">
            <div class="px-10 pb-8">
              <text-input v-model="shipping.firstName" :required="true" :errors="$page.errors.first_name" class="mt-4"
                          label="Prenume" type="text" autofocus autocapitalize="on"/>
              <text-input v-model="shipping.lastName" :required="true" :errors="$page.errors.last_name" class="mt-4"
                          label="Nume" type="text" autocapitalize="on"/>
              <text-input v-model="shipping.phoneNumber" :required="true" :errors="$page.errors.phone_number"
                          class="mt-4" label="Telefon" type="text"/>
              <text-input v-model="shipping.company" :errors="$page.errors.company" class="mt-4" label="Companie"
                          type="text"/>
              <text-input v-model="shipping.cif" :errors="$page.errors.cif" class="mt-4" label="CIF" type="text"/>
              <text-input v-model="shipping.address" :required="true" :errors="$page.errors.address" class="mt-4"
                          label="Adresă" type="text"/>
              <text-input v-model="shipping.postalCode" :required="true" :errors="$page.errors.postal_code" class="mt-4"
                          label="Cod poștal" type="text"/>
              <select-input @input="getCountryRegions('RO', 'RO')" ref="shipping-country" :key="shipping.country"
                            v-model="shipping.country" :required="true" :errors="$page.errors.country" class="mt-4"
                            label="Țară">
                <option v-for="country in countries" v-if="country.code != 'RO'" :key="country.code"
                        :value="country.code">
                  {{ country.name }}
                </option>
              </select-input>
              <text-input v-model="shippingRegionSelect" :required="true" :errors="$page.errors.region" class="mt-4"
                          label="Regiune" type="text"/>
              <text-input v-model="shipping.city" :required="true" :errors="$page.errors.city" class="mt-4" label="City"
                          type="text"/>
            </div>
          </form>
        </div>
      </div>
      <div class="w-full mt-4 py-4 bg-grey-100 border-grey-200 flex justify-end">
        <span class="text-2xl">Total: <span class="font-bold">&euro; {{
            number_format($page.cart.total, 0, 0, '.')
          }}</span></span>
      </div>
      <div class="w-full py-4 bg-grey-100 border-grey-200 flex justify-end">
        <svg class="w-4 h-4 mt-2" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
             viewBox="0 0 24 24" stroke="currentColor">
          <path d="M15 19l-7-7 7-7"></path>
        </svg>
        <button @click="previousStep" class="btn-gray text-gray-700 bg-transparent hover:bg-transparent"
                style="padding: 6px 12px 6px 0px; color: currentColor !important; ">
          <span>Înapoi la facturare</span>
        </button>
        <button @click="nextStep" class="btn-gray" type="submit">
          <span>Pasul următor</span>
        </button>
      </div>
    </div>
    <div v-if="checkoutStep === 3" class="flex items-center flex-wrap">
      <h1 class="w-full font-bold text-3xl mb-2 mt-8">Pasul 3 - Plată</h1>
      <div class="w-full max-w-lg px-10">
        <select-input v-model="paymentMethod" :key="paymentMethod" :required="true"
                      :errors="$page.errors.payment_method" class="mt-4" label="Metoda de plată">
          <option :value="$page.constants.order.payment_methods.card.value">
            {{ $page.constants.order.payment_methods.card.text }}
          </option>
          <option :value="$page.constants.order.payment_methods.bank_transfer.value">
            {{ $page.constants.order.payment_methods.bank_transfer.text }}
          </option>
          <option :value="$page.constants.order.payment_methods.cash.value">
            {{ $page.constants.order.payment_methods.cash.text }}
          </option>
        </select-input>
      </div>
      <div class="w-full mt-4 py-4 bg-grey-100 border-grey-200 flex justify-end">
        <span class="text-2xl">Total: <span class="font-bold">&euro; {{
            number_format($page.cart.total, 0, 0, '.')
          }}</span></span>
      </div>
      <div class="w-full py-4 bg-grey-100 border-grey-200 flex justify-end">
        <svg class="w-4 h-4 mt-2" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
             viewBox="0 0 24 24" stroke="currentColor">
          <path d="M15 19l-7-7 7-7"></path>
        </svg>
        <button @click="previousStep"
                class="btn-gray text-gray-700 bg-transparent hover:bg-transparent hover:text-gray-900"
                style="padding: 6px 12px 6px 0px; color: currentColor !important; ">
          <span>Înapoi la livrare</span>
        </button>
        <loading-button @click.native="submit" :loading="sending" class="btn-gray" type="submit">
          <span>Plasează comanda</span>
        </loading-button>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import axios from "axios";

export default {
  metaInfo() {
    return {
      title: 'Finalizare comandă',
      meta: [
        {name: 'robots', content: 'noindex, nofollow, nosnippet, noarchive'},
        {name: 'googlebot', content: 'noindex, nofollow, nosnippet, noarchive'}
      ]
    }
  },
  layout: Layout,
  components: {
    Layout,
    LoadingButton,
    SelectInput,
    TextInput
  },
  watch: {
    billingRegionSelect: function (val) {
      if (val.isInteger) {
        this.billing.regionId = val
        this.billing.region = this.billing_regions.find(o => o.id == val).name
      } else {
        this.billing.region = val

      }
    },
    shippingRegionSelect: function (val) {
      if (val.isInteger) {
        this.shipping.regionId = val
        this.shipping.region = this.shipping_regions[val]
      } else {
        this.shipping.regionId = val
        this.shipping.region = this.shipping_regions.find(o => o.id == val).name
      }
    },
    showAddressForm: function (val) {
      if (val) {
        this.showAddressForm = true
      } else {
        this.showAddressForm = false
      }
    },
    billingSelectedAddressId: function (val) {
      let selectedAddress = this.$page.auth.user.addresses.filter((address) => {
        return address.id == val
      })[0]

      this.billing.firstName = selectedAddress.first_name
      this.billing.lastName = selectedAddress.last_name
      this.billing.phoneNumber = selectedAddress.phone_number
      this.billing.company = selectedAddress.company
      this.billing.cif = selectedAddress.cif
      this.billing.address = selectedAddress.address
      this.billing.postalCode = selectedAddress.postal_code
      this.billing.country = selectedAddress.country_id
      this.getCountryRegions(selectedAddress.country_id, this.billing.country ? this.billing.country : ' ')
      this.billing.regionId = selectedAddress.region_id
      this.billing.region = selectedAddress.region
      this.billing.city = selectedAddress.city
    },
    shippingSelectedAddressId: function (val) {
      if (val !== 0) {
        let selectedAddress = this.$page.auth.user.addresses.filter((address) => {
          return address.id == val
        })[0]

        this.shipping.firstName = selectedAddress.first_name
        this.shipping.lastName = selectedAddress.last_name
        this.shipping.phoneNumber = selectedAddress.phone_number
        this.shipping.company = selectedAddress.company
        this.shipping.cif = selectedAddress.cif
        this.shipping.address = selectedAddress.address
        this.shipping.postalCode = selectedAddress.postal_code
        this.shipping.country = selectedAddress.country_id
        this.getCountryRegions(this.shipping.country ? this.shipping.country : ' ', selectedAddress.country_id)
        this.shipping.regionId = selectedAddress.region_id
        this.shipping.region = selectedAddress.region
        this.shipping.city = selectedAddress.city
      } else {
        this.showAddressForm = true
        let selectedAddress = null
        this.shipping.firstName = ''
        this.shipping.lastName = ''
        this.shipping.phoneNumber = ''
        this.shipping.company = ''
        this.shipping.cif = ''
        this.shipping.address = ''
        this.shipping.postalCode = ''
        this.shipping.country = ''
        this.billing_regions = null
        this.shipping_regions = null
        this.shipping.regionId = ''
        this.shipping.region = ''
        this.shipping.city = ''
      }
    }
  },
  computed: {
    console: () => console,
    billingType() {
      if (this.billing.company && this.billing.cif) {
        return this.$page.constants.order.address_types.company.value
      }
      return this.$page.constants.order.address_types.person.value
    },
    shippingType() {
      // if (this.shipping.company && this.shipping.cif) {
      //   return this.$page.constants.order.address_types.company.value
      // }
      return this.$page.constants.order.address_types.person.value
    }
  },
  data() {
    return {
      billing_regions: null,
      shipping_regions: null,
      billingRegionSelect: null,
      shippingRegionSelect: null,
      checkoutStep: 1,
      sending: false,
      billingSelectedAddressId: 0,
      shippingSelectedAddressId: null,
      showAddressForm: false,
      deliveryOption: null,
      paymentMethod: null,
      billing: {
        firstName: null,
        lastName: null,
        phoneNumber: null,
        company: null,
        email: null,
        cif: null,
        address: null,
        postalCode: null,
        country: null,
        regionId: null,
        region: null,
        city: null,
      },
      shipping: {
        firstName: null,
        lastName: null,
        phoneNumber: null,
        company: null,
        cif: null,
        address: null,
        postalCode: null,
        country: null,
        regionId: null,
        region: null,
        city: null,
      },
      countries: [{code: 'AF', name: 'Afganistan'},
        {code: 'ZA', name: 'Africa de Sud'},
        {code: 'AL', name: 'Albania'},
        {code: 'DZ', name: 'Algeria'},
        {code: 'AD', name: 'Andorra'},
        {code: 'AO', name: 'Angola'},
        {code: 'AI', name: 'Anguilla'},
        {code: 'AQ', name: 'Antarctica'},
        {code: 'AG', name: 'Antigua și Barbuda'},
        {code: 'AN', name: 'Antilele Olandeze'},
        {code: 'SA', name: 'Arabia Saudită'},
        {code: 'AR', name: 'Argentina'},
        {code: 'AM', name: 'Armenia'},
        {code: 'AW', name: 'Aruba'},
        {code: 'AU', name: 'Australia'},
        {code: 'AT', name: 'Austria'},
        {code: 'AZ', name: 'Azerbaidjan'},
        {code: 'BS', name: 'Bahamas'},
        {code: 'BH', name: 'Bahrain'},
        {code: 'BD', name: 'Bangladesh'},
        {code: 'BB', name: 'Barbados'},
        {code: 'BE', name: 'Belgia'},
        {code: 'BZ', name: 'Belize'},
        {code: 'BJ', name: 'Benin'},
        {code: 'BM', name: 'Bermuda'},
        {code: 'BT', name: 'Bhutan'},
        {code: 'BY', name: 'Bielorusia'},
        {code: 'BO', name: 'Bolivia'},
        {code: 'BA', name: 'Bosnia și Herțegovina'},
        {code: 'BW', name: 'Botswana'},
        {code: 'BR', name: 'Brazilia'},
        {code: 'BN', name: 'Brunei'},
        {code: 'BG', name: 'Bulgaria'},
        {code: 'BF', name: 'Burkina Faso'},
        {code: 'BI', name: 'Burundi'},
        {code: 'KH', name: 'Cambodgia'},
        {code: 'CM', name: 'Camerun'},
        {code: 'CA', name: 'Canada'},
        {code: 'CV', name: 'Capul Verde'},
        {code: 'CL', name: 'Chile'},
        {code: 'CN', name: 'China'},
        {code: 'TD', name: 'Ciad'},
        {code: 'CY', name: 'Cipru'},
        {code: 'CI', name: 'Coasta de Fildeș'},
        {code: 'CO', name: 'Columbia'},
        {code: 'KM', name: 'Comore'},
        {code: 'CG', name: 'Congo'},
        {code: 'KP', name: 'Coreea de Nord'},
        {code: 'KR', name: 'Coreea de Sud'},
        {code: 'CR', name: 'Costa Rica'},
        {code: 'HR', name: 'Croația'},
        {code: 'CU', name: 'Cuba'},
        {code: 'DK', name: 'Danemarca'},
        {code: 'DJ', name: 'Djibouti'},
        {code: 'DM', name: 'Dominica'},
        {code: 'EC', name: 'Ecuador'},
        {code: 'EG', name: 'Egipt'},
        {code: 'CH', name: 'Eleveția'},
        {code: 'SV', name: 'El Salvador'},
        {code: 'AE', name: 'Emiratele Arabe Unite'},
        {code: 'ER', name: 'Eritreea'},
        {code: 'EE', name: 'Estonia'},
        {code: 'ET', name: 'Etiopia'},
        {code: 'FJ', name: 'Fiji'},
        {code: 'PH', name: 'Filipine'},
        {code: 'FI', name: 'Finlanda'},
        {code: 'FR', name: 'Franța'},
        {code: 'GA', name: 'Gabon'},
        {code: 'GM', name: 'Gambia'},
        {code: 'GE', name: 'Georgia'},
        {code: 'DE', name: 'Germania'},
        {code: 'GH', name: 'Ghana'},
        {code: 'GI', name: 'Gibraltar'},
        {code: 'GR', name: 'Grecia'},
        {code: 'GD', name: 'Grenada'},
        {code: 'GL', name: 'Groenlanda'},
        {code: 'GP', name: 'Guadelupa'},
        {code: 'GU', name: 'Guam'},
        {code: 'GT', name: 'Guatemala'},
        {code: 'GG', name: 'Guernsey'},
        {code: 'GN', name: 'Guineea'},
        {code: 'GW', name: 'Guineea-Bissau'},
        {code: 'GQ', name: 'Guineea Ecuatorială'},
        {code: 'GY', name: 'Guyana'},
        {code: 'GF', name: 'Guyana Franceză'},
        {code: 'HT', name: 'Haiti'},
        {code: 'HN', name: 'Honduras'},
        {code: 'IN', name: 'India'},
        {code: 'ID', name: 'Indonezia'},
        {code: 'BV', name: 'Insula Bouvet'},
        {code: 'CX', name: 'Insula Christmas'},
        {code: 'HM', name: 'Insula Heard și Insulele McDonald'},
        {code: 'IM', name: 'Insula Man'},
        {code: 'AX', name: 'Insulele Aland'},
        {code: 'KY', name: 'Insulele Cayman'},
        {code: 'CC', name: 'Insulele Cocos'},
        {code: 'CK', name: 'Insulele Cook'},
        {code: 'FK', name: 'Insulele Falkland'},
        {code: 'FO', name: 'Insulele Feroe'},
        {code: 'GS', name: 'Insulele Georgia de Sud și Sandwich de Sud'},
        {code: 'MP', name: 'Insulele Mariane de Nord'},
        {code: 'MH', name: 'Insulele Marshall'},
        {code: 'UM', name: 'Insulele mici îndepărtate ale Statelor Unite ale Americii'},
        {code: 'NF', name: 'Insulele Norfolk'},
        {code: 'SB', name: 'Insulele Solomon'},
        {code: 'TC', name: 'Insulele Turks și Caicos'},
        {code: 'VG', name: 'Insulele Virgine Britanice'},
        {code: 'VI', name: 'Insulele Virgine S.U.A.'},
        {code: 'JO', name: 'Iordania'},
        {code: 'IQ', name: 'Irak'},
        {code: 'IR', name: 'Iran'},
        {code: 'IE', name: 'Irlanda'},
        {code: 'IS', name: 'Islanda'},
        {code: 'IL', name: 'Israel'},
        {code: 'IT', name: 'Italia'},
        {code: 'JM', name: 'Jamaica'},
        {code: 'JP', name: 'Japonia'},
        {code: 'JE', name: 'Jersey'},
        {code: 'KZ', name: 'Kazahstan'},
        {code: 'KG', name: 'Kârgâzstan'},
        {code: 'KE', name: 'Kenya'},
        {code: 'KI', name: 'Kiribati'},
        {code: 'KW', name: 'Kuweit'},
        {code: 'LA', name: 'Laos'},
        {code: 'LS', name: 'Lesotho'},
        {code: 'LV', name: 'Letonia'},
        {code: 'LB', name: 'Liban'},
        {code: 'LR', name: 'Liberia'},
        {code: 'LY', name: 'Libia'},
        {code: 'LI', name: 'Liechtenstein'},
        {code: 'LT', name: 'Lituania'},
        {code: 'LU', name: 'Luxemburg'},
        {code: 'MK', name: 'Macedonia'},
        {code: 'MG', name: 'Madagascar'},
        {code: 'MY', name: 'Malaezia'},
        {code: 'MW', name: 'Malawi'},
        {code: 'MV', name: 'Maldive'},
        {code: 'ML', name: 'Mali'},
        {code: 'MT', name: 'Malta'},
        {code: 'GB', name: 'Marea Britanie'},
        {code: 'MA', name: 'Maroc'},
        {code: 'MQ', name: 'Martinica'},
        {code: 'MR', name: 'Mauritania'},
        {code: 'MU', name: 'Mauritius'},
        {code: 'YT', name: 'Mayotte'},
        {code: 'MX', name: 'Mexic'},
        {code: 'FM', name: 'Micronezia'},
        {code: 'MC', name: 'Monaco'},
        {code: 'MN', name: 'Mongolia'},
        {code: 'MS', name: 'Montserrat'},
        {code: 'MZ', name: 'Mozambic'},
        {code: 'ME', name: 'Muntenegru'},
        {code: 'MM', name: 'Myanmar'},
        {code: 'NA', name: 'Namibia'},
        {code: 'NR', name: 'Nauru'},
        {code: 'NP', name: 'Nepal'},
        {code: 'NI', name: 'Nicaragua'},
        {code: 'NE', name: 'Niger'},
        {code: 'NG', name: 'Nigeria'},
        {code: 'NU', name: 'Niue'},
        {code: 'NO', name: 'Norvegia'},
        {code: 'NC', name: 'Noua Caledonie'},
        {code: 'NZ', name: 'Noua Zeelandă'},
        {code: 'NL', name: 'Olanda'},
        {code: 'OM', name: 'Oman'},
        {code: 'PK', name: 'Pakistan'},
        {code: 'PW', name: 'Palau'},
        {code: 'PA', name: 'Panama'},
        {code: 'PG', name: 'Papua Noua Guinee'},
        {code: 'PY', name: 'Paraguay'},
        {code: 'PE', name: 'Peru'},
        {code: 'PN', name: 'Pitcairn'},
        {code: 'PF', name: 'Polinezia Franceză'},
        {code: 'PL', name: 'Polonia'},
        {code: 'PR', name: 'Porto Rico'},
        {code: 'PT', name: 'Portugalia'},
        {code: 'QA', name: 'Qatar'},
        {code: 'HK', name: 'R.A.S. Hong Kong a Chinei'},
        {code: 'MO', name: 'R.A.S. Macao a Chinei'},
        {code: 'CZ', name: 'Republica Cehă'},
        {code: 'CF', name: 'Republica Centrafricană'},
        {code: 'CD', name: 'Republica Democrată Congo'},
        {code: 'DO', name: 'Republica Dominicană'},
        {code: 'MD', name: 'Republica Moldova'},
        {code: 'RE', name: 'Reunion'},
        {code: 'RO', name: 'România'},
        {code: 'RU', name: 'Rusia'},
        {code: 'RW', name: 'Rwanda'},
        {code: 'EH', name: 'Sahara Occidentală'},
        {code: 'WS', name: 'Samoa'},
        {code: 'AS', name: 'Samoa Americană'},
        {code: 'SM', name: 'San Marino'},
        {code: 'ST', name: 'Sao Tome și Principe'},
        {code: 'SN', name: 'Senegal'},
        {code: 'RS', name: 'Serbia'},
        {code: 'SC', name: 'Seychelles'},
        {code: 'SH', name: 'Sfânta Elena'},
        {code: 'LC', name: 'Sfânta Lucia'},
        {code: 'BL', name: 'Sfântul Bartolomeu'},
        {code: 'KN', name: 'Sfântul Kitts și Nevis'},
        {code: 'MF', name: 'Sfântul Martin'},
        {code: 'PM', name: 'Sfântul Pierre și Miquelon'},
        {code: 'VC', name: 'Sfântul Vincent și Grenadine'},
        {code: 'SL', name: 'Sierra Leone'},
        {code: 'SG', name: 'Singapore'},
        {code: 'SY', name: 'Siria'},
        {code: 'SK', name: 'Slovacia'},
        {code: 'SI', name: 'Slovenia'},
        {code: 'SO', name: 'Somalia'},
        {code: 'ES', name: 'Spania'},
        {code: 'LK', name: 'Sri Lanka'},
        {code: 'US', name: 'Statele Unite ale Americii'},
        {code: 'SD', name: 'Sudan'},
        {code: 'SE', name: 'Suedia'},
        {code: 'SR', name: 'Surinam'},
        {code: 'SJ', name: 'Svalbard și Jan Mayen'},
        {code: 'SZ', name: 'Swaziland'},
        {code: 'TJ', name: 'Tadjikistan'},
        {code: 'TW', name: 'Taiwan'},
        {code: 'TZ', name: 'Tanzania'},
        {code: 'TF', name: 'Teritoriile Australe și Antarctice Franceze'},
        {code: 'IO', name: 'Teritoriul Britanic din Oceanul Indian'},
        {code: 'PS', name: 'Teritoriul Palestinian'},
        {code: 'TH', name: 'Thailanda'},
        {code: 'TL', name: 'Timorul de Est'},
        {code: 'TG', name: 'Togo'},
        {code: 'TK', name: 'Tokelau'},
        {code: 'TO', name: 'Tonga'},
        {code: 'TT', name: 'Trinidad-Tobago'},
        {code: 'TN', name: 'Tunisia'},
        {code: 'TR', name: 'Turcia'},
        {code: 'TM', name: 'Turkmenistan'},
        {code: 'TV', name: 'Tuvalu'},
        {code: 'UA', name: 'Ucraina'},
        {code: 'UG', name: 'Uganda'},
        {code: 'HU', name: 'Ungaria'},
        {code: 'UY', name: 'Uruguay'},
        {code: 'UZ', name: 'Uzbekistan'},
        {code: 'VU', name: 'Vanuatu'},
        {code: 'VA', name: 'Vatican'},
        {code: 'VE', name: 'Venezuela'},
        {code: 'VN', name: 'Vietnam'},
        {code: 'WF', name: 'Wallis și Futuna'},
        {code: 'YE', name: 'Yemen'},
        {code: 'ZM', name: 'Zambia'},
        {code: 'ZW', name: 'Zimbabwe'}]
    }
  },
  methods: {
    setDelivery(deliveryId) {
      this.deliveryOption = deliveryId;
      if(deliveryId == 2){
        this.shipping.country = 'RO'
        this.getCountryRegions('RO', 'RO')
      }
    },
    nextStep() {
      this.checkoutStep++
      window.scrollTo(0, 0)
    },
    previousStep() {
      this.checkoutStep--
      window.scrollTo(0, 0)
    },
    submit() {
      this.sending = true
      if (this.paymentMethod === this.$page.constants.order.payment_methods.card.value) {
        this.mobilpayCheckout()
        return
      }
      this.checkout()
    },
    mobilpayCheckout() {
      this.$inertia.post(this.route('shop.checkout.mobilpay'), {
        payment_method: this.paymentMethod,
        delivery_option: this.deliveryOption,
        billing_type: this.billingType,
        billing_first_name: this.billing.firstName,
        billing_last_name: this.billing.lastName,
        billing_email: this.billing.email,
        billing_phone_number: this.billing.phoneNumber,
        billing_company: this.billing.company,
        billing_cif: this.billing.cif,
        billing_address: this.billing.address,
        billing_postal_code: this.billing.postalCode,
        billing_country: this.billing.country,
        billing_region_id: this.billing.regionId,
        billing_region: this.billing.region,
        billing_city: this.billing.city,
        shipping_type: this.shippingType,
        shipping_first_name: !this.showAddressForm ? this.billing.firstName : this.shipping.firstName,
        shipping_last_name: !this.showAddressForm ? this.billing.lastName : this.shipping.lastName,
        shipping_phone_number: !this.showAddressForm ? this.billing.phoneNumber : this.shipping.phoneNumber,
        shipping_company: !this.showAddressForm ? this.billing.company : this.shipping.company,
        shipping_cif: !this.showAddressForm ? this.billing.cif : this.shipping.cif,
        shipping_address: !this.showAddressForm ? this.billing.address : this.shipping.address,
        shipping_postal_code: !this.showAddressForm ? this.billing.postalCode : this.shipping.postalCode,
        shipping_country: !this.showAddressForm ? this.billing.country : this.shipping.country,
        shipping_region_id: !this.showAddressForm ? this.billing.regionId : this.shipping.regionId,
        shipping_region: !this.showAddressForm ? this.billing.region : this.shipping.region,
        shipping_city: !this.showAddressForm ? this.billing.city : this.shipping.city
      }).then(() => this.sending = false)
    },
    checkout() {
      this.$inertia.post(this.route('shop.checkout'), {
        payment_method: this.paymentMethod,
        delivery_option: this.deliveryOption,
        billing_type: this.billingType,
        billing_first_name: this.billing.firstName,
        billing_last_name: this.billing.lastName,
        billing_phone_number: this.billing.phoneNumber,
        billing_email: this.billing.email,
        billing_company: this.billing.company,
        billing_cif: this.billing.cif,
        billing_address: this.billing.address,
        billing_postal_code: this.billing.postalCode,
        billing_country: this.billing.country,
        billing_region_id: this.billing.regionId,
        billing_region: this.billing.region,
        billing_city: this.billing.city,
        shipping_type: this.shippingType,
        shipping_first_name: !this.showAddressForm ? this.billing.firstName : this.shipping.firstName,
        shipping_last_name: !this.showAddressForm ? this.billing.lastName : this.shipping.lastName,
        shipping_phone_number: !this.showAddressForm ? this.billing.phoneNumber : this.shipping.phoneNumber,
        shipping_company: !this.showAddressForm ? this.billing.company : this.shipping.company,
        shipping_cif: !this.showAddressForm ? this.billing.cif : this.shipping.cif,
        shipping_address: !this.showAddressForm ? this.billing.address : this.shipping.address,
        shipping_postal_code: !this.showAddressForm ? this.billing.postalCode : this.shipping.postalCode,
        shipping_country: !this.showAddressForm ? this.billing.country : this.shipping.country,
        shipping_region_id: !this.showAddressForm ? this.billing.regionId : this.shipping.regionId,
        shipping_region: !this.showAddressForm ? this.billing.region : this.shipping.region,
        shipping_city: !this.showAddressForm ? this.billing.city : this.shipping.city
      })
    },
    getCountryRegions(billingCountry, shippingCountry) {
      axios({
        method: "post",
        url: "/api/get-regions",
        data: {
          billing_country: billingCountry && shippingCountry && this.checkoutStep === 1 ? billingCountry : this.checkoutStep === 1 ? this.$refs['billing-country'].selected : null,
          shipping_country: billingCountry && shippingCountry ? shippingCountry : this.checkoutStep === 2 ? this.$refs['shipping-country'].selected : null
        },
      }).then((response) => { //succes
        this.billing_regions = response.data.billing_regions
        this.shipping_regions = response.data.shipping_regions
      }).catch(function (error) {
      });
      // this.$inertia.visit(route('shop.checkout'), {
      //   data: {
      //     billing_country: billingCountry && shippingCountry && this.checkoutStep === 1 ? billingCountry : this.checkoutStep === 1 ? this.$refs['billing-country'].selected : null,
      //     shipping_country: billingCountry && shippingCountry ? shippingCountry : this.checkoutStep === 2 ? this.$refs['shipping-country'].selected : null
      //   },
      //   replace: false,
      //   preserveState: true,
      //   preserveScroll: true,
      // })
    },
    number_format(number, decimals, dec_point, thousands_sep) {
      // Strip all characters but numerical ones.
      number = (number + '').replace(/[^0-9+\-Ee.]/g, '');
      var n = !isFinite(+number) ? 0 : +number,
          prec = !isFinite(+decimals) ? 0 : Math.abs(decimals),
          sep = (typeof thousands_sep === 'undefined') ? ',' : thousands_sep,
          dec = (typeof dec_point === 'undefined') ? '.' : dec_point,
          s = '',
          toFixedFix = function (n, prec) {
            var k = Math.pow(10, prec);
            return '' + Math.round(n * k) / k;
          };
      // Fix for IE parseFloat(0.55).toFixed(0) = 0;
      s = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.');
      if (s[0].length > 3) {
        s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
      }
      if ((s[1] || '').length < prec) {
        s[1] = s[1] || '';
        s[1] += new Array(prec - s[1].length + 1).join('0');
      }
      return s.join(dec);
    }
  },
  mounted() {
    this.deliveryOption = this.$page.constants.order.delivery_options.showroom.value
    this.paymentMethod = this.$page.constants.order.payment_methods.card.value
  }
}
</script>
